import { Player } from "@player";
import type { CompletedProgramName } from "@enums";
import { GetAllServers } from "../Server/AllServers";
import {
  ACHIEVEMENT_TO_LOCATION,
  backdoorLocation,
  darkwebPurchaseLocation,
  factionJoinLocation,
  programCreationLocation,
} from "./Data";

/**
 * Locations recorded by an event rather than derived from game state, waiting to be sent.
 *
 * recordSatisfiedLocations() drains this. It exists because the sweep can only report a location as
 * newly satisfied once, and an event-recorded location is written to the persisted list the instant it
 * happens -- so by the time the next sweep runs it already looks like old news.
 */
let pendingDirectLocations: string[] = [];

/**
 * Every Archipelago location this save currently satisfies, derived from game state.
 *
 * Deriving rather than reacting to events means no code path can be missed: a backdoor installed by
 * the terminal, by the singularity API, by an NS helper or by the dev menu all look the same here.
 *
 * This is a snapshot of what is true *now*, which is not the same as everything that has ever been
 * true. Backdoors are wiped by installing augmentations, so callers must accumulate these results
 * rather than treating each sweep as the complete picture. That accumulation is what
 * Player.archipelagoCheckedLocations holds.
 */
export function collectSatisfiedLocations(): string[] {
  const locations: string[] = [];

  for (const achievement of Player.achievements) {
    const locationName = ACHIEVEMENT_TO_LOCATION[achievement.ID];
    if (locationName !== undefined) {
      locations.push(locationName);
    }
  }

  // Excludes the darkweb, which is not a backdoor target. Purchased servers and home can appear
  // here, but they have no matching location and are dropped when the name is resolved to an id.
  for (const server of GetAllServers()) {
    if (server.backdoorInstalled) {
      locations.push(backdoorLocation(server.hostname));
    }
  }

  // Membership is wiped by installing augmentations, exactly like a backdoor: prestigeAugmentation()
  // clears Player.factions (src/PersonObjects/Player/PlayerObjectGeneralMethods.ts:111) and only
  // re-invites factions flagged keepOnInstall. So these are no more durable than backdoors, and the
  // accumulation into Player.archipelagoCheckedLocations is what stops an install losing the check.
  for (const faction of Player.factions) {
    locations.push(factionJoinLocation(faction));
  }

  return locations;
}

/**
 * Records a location satisfied by something that happened, rather than by the state it left behind.
 *
 * Writing to the persisted list immediately -- rather than only queueing -- is what covers an event
 * that happens while disconnected: resendAllChecks() replays the whole list on the next successful
 * login, so the check survives even if the queue never gets drained.
 */
function recordDirectLocation(locationName: string): void {
  if (Player.archipelagoCheckedLocations.includes(locationName)) {
    return;
  }

  Player.archipelagoCheckedLocations.push(locationName);
  pendingDirectLocations.push(locationName);
}

/**
 * Records a darkweb purchase.
 *
 * Buying a program cannot be derived the way a backdoor can: owning a program says nothing about how
 * it was obtained, and the player may well have been given it by Archipelago or by an augmentation.
 * So this is recorded where it happens instead.
 */
export function recordDarkwebPurchase(program: CompletedProgramName): void {
  recordDirectLocation(darkwebPurchaseLocation(program));
}

/**
 * Records writing a program.
 *
 * The other half of the acquisition story, and event-recorded for the same reason a purchase is:
 * having the file says nothing about how it was obtained. Buying and writing are separate checks, so
 * a player who does both sends both.
 *
 * Only the 10 programs that are both creatable and purchasable have a location. Anything else --
 * NUKE.exe, b1t_flum3.exe, or a program this fork adds -- resolves to a name with no id and is
 * dropped when sent, so this deliberately does not filter.
 */
export function recordProgramCreation(program: CompletedProgramName): void {
  recordDirectLocation(programCreationLocation(program));
}

/**
 * Discards event-recorded locations that have not been sent yet.
 *
 * Called when the save is attached to a different multiworld, where a location satisfied against the
 * previous seed no longer means anything.
 */
export function clearPendingLocations(): void {
  pendingDirectLocations = [];
}

/**
 * Adds newly satisfied locations to the persisted list, and returns just the ones that were new.
 *
 * Kept separate from sending so that checks are recorded even while disconnected. A location that
 * becomes true during an outage is captured here and survives both a reconnect and an augmentation
 * install, which is what makes the checks idempotent rather than merely repeated.
 */
export function recordSatisfiedLocations(): string[] {
  const alreadyChecked = new Set(Player.archipelagoCheckedLocations);
  const newlyChecked: string[] = [];

  // Drained unconditionally: these are already in the persisted list, so the loop below would filter
  // them out as things this save has seen before.
  newlyChecked.push(...pendingDirectLocations);
  pendingDirectLocations = [];

  for (const locationName of collectSatisfiedLocations()) {
    if (!alreadyChecked.has(locationName)) {
      alreadyChecked.add(locationName);
      newlyChecked.push(locationName);
      Player.archipelagoCheckedLocations.push(locationName);
    }
  }

  return newlyChecked;
}
